// Display-only labels for the HMRC band that fired against a given taxable profit.
// SQL (fn_get_tax_pot / fn_get_ni_pot) remains the single source of truth for the
// math; these constants exist purely so the UI can name the rule. If HMRC bands
// change, update SQL first — drift here only mislabels copy, never miscalculates.

const PERSONAL_ALLOWANCE = 12570;
const BASIC_LIMIT = 50270;
const HIGHER_LIMIT = 125140;

export type IncomeTaxBand = 'none' | 'basic' | 'higher' | 'additional';
export type NicBand = 'none' | 'main' | 'upper';

export const incomeTaxBand = (taxableProfit: number): IncomeTaxBand => {
    if (taxableProfit <= PERSONAL_ALLOWANCE) return 'none';
    if (taxableProfit <= BASIC_LIMIT) return 'basic';
    if (taxableProfit <= HIGHER_LIMIT) return 'higher';
    return 'additional';
};

export const incomeTaxBandLabel = (taxableProfit: number, region: 'rUK' | 'scotland' = 'rUK'): string => {
    if (region === 'scotland') {
        if (taxableProfit <= 12570) return 'Under personal allowance';
        if (taxableProfit <= 16537) return '19% starter rate';
        if (taxableProfit <= 29526) return '20% basic rate';
        if (taxableProfit <= 43662) return '21% intermediate rate';
        if (taxableProfit <= 75000) return '42% higher rate';
        if (taxableProfit <= 125140) return '45% advanced rate';
        return '48% top rate';
    }
    switch (incomeTaxBand(taxableProfit)) {
        case 'none':       return 'Under personal allowance';
        case 'basic':      return '20% basic rate';
        case 'higher':     return '20% basic + 40% over £50,270';
        case 'additional': return '+ 45% over £125,140';
    }
};

export const nicBand = (taxableProfit: number): NicBand => {
    if (taxableProfit <= PERSONAL_ALLOWANCE) return 'none';
    if (taxableProfit <= BASIC_LIMIT) return 'main';
    return 'upper';
};

export const nicBandLabel = (taxableProfit: number): string => {
    switch (nicBand(taxableProfit)) {
        case 'none':  return 'Under Class 4 threshold';
        case 'main':  return '6% on profit over £12,570';
        case 'upper': return '6% main + 2% over £50,270';
    }
};

export const vatBandLabel = (isVatRegistered: boolean): string =>
    isVatRegistered ? '20% on vatable sales' : 'Not VAT-registered';

// "Payable by" date for the current tax year's Self-Assessment.
// UK tax year: 6 Apr → 5 Apr; SA payment deadline is 31 Jan following the
// year-end. E.g. tax year ending 5 Apr 2027 → SA payable by 31 Jan 2028.
export const currentTaxYearLabel = (now: Date = new Date()): string => {
    const m = now.getMonth();
    const d = now.getDate();
    const y = now.getFullYear();
    const beforeApr6 = m < 3 || (m === 3 && d < 6);
    const startYear = beforeApr6 ? y - 1 : y;
    const endYear = startYear + 1;
    return `${startYear}/${String(endYear).slice(-2)}`;
};

export const selfAssessmentDueLabel = (now: Date = new Date()): string => {
    const m = now.getMonth();
    const d = now.getDate();
    const y = now.getFullYear();
    const beforeApr6 = m < 3 || (m === 3 && d < 6);
    const startYear = beforeApr6 ? y - 1 : y;
    // Tax year ends 5 Apr (startYear + 1); SA payable 31 Jan (startYear + 2).
    return `31 Jan ${startYear + 2}`;
};

export const taxYearStartLabel = (now: Date = new Date()): string => {
    const m = now.getMonth();
    const d = now.getDate();
    const y = now.getFullYear();
    const beforeApr6 = m < 3 || (m === 3 && d < 6);
    const startYear = beforeApr6 ? y - 1 : y;
    return `6 Apr ${startYear}`;
};
